import { useDraftResumeToast } from "@/hooks/useDraftResumeToast";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  PlusCircle, Search, Filter, ChevronDown, ArrowUpDown,
  Mail, Phone, Users, Dumbbell, ChevronRight, Bell, BellOff
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";

import { AddCoachDialog } from "@/components/coaches/AddCoachDialog";
import { useCoachesSupabaseStore } from "@/store/api/coaches";
import { useUserStore } from "@/store/userStore";
import { useCoachDeviceStatus } from "@/hooks/useCoachDeviceStatus";

// --- petit utilitaire pour le débouonçage de la recherche ---
function useDebounced<T>(value: T, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

type SortKey = "name-asc" | "name-desc" | "specialty-asc";

const Coaches = () => {
  const { t } = useTranslation(["coaches", "common"]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 250);
  const [sortBy, setSortBy] = useState<SortKey>("name-asc");
  const [specialtyFilter, setSpecialtyFilter] = useState<string>("all");
  const [isAddCoachOpen, setIsAddCoachOpen] = useState(false);

  // Saisie coach interrompue par un reload → proposer de la reprendre.
  useDraftResumeToast([
    {
      formId: "add-coach",
      message: t("draftUnsaved", { defaultValue: "Saisie coach non enregistrée" }),
      description: (d) =>
        [d.firstName, d.lastName].filter(Boolean).join(" ") || (d.email as string) || undefined,
      isEmpty: (d) => !(d.firstName || d.lastName || d.email),
      onResume: () => setIsAddCoachOpen(true),
    },
  ]);

  const { coaches, isLoading, fetchCoaches } = useCoachesSupabaseStore();
  const gymId = useUserStore((state) => state.gymId);

  // Get device status for all coaches
  const coachUserIds = useMemo(() => coaches.map((c) => c.user_id).filter(Boolean) as string[], [coaches]);
  const { data: deviceStatus } = useCoachDeviceStatus(coachUserIds);

  // gymId en dépendance : `fetchCoaches` étant stable, la liste restait celle
  // de la salle précédente après un changement de salle.
  useEffect(() => { fetchCoaches(); }, [fetchCoaches, gymId]);

  const normalize = (s?: string) => (s || "").toLowerCase();

  const specialties = useMemo(
    () => Array.from(new Set(coaches.map((c) => c.specialization).filter(Boolean))).sort(),
    [coaches]
  );

  const filtered = useMemo(() => {
    const q = normalize(debouncedSearch);
    let rows = coaches.filter((c) => {
      if (specialtyFilter !== "all" && c.specialization !== specialtyFilter) return false;
      if (!q) return true;
      return (
        normalize(c.firstName).includes(q) ||
        normalize(c.lastName).includes(q) ||
        normalize(c.specialization).includes(q) ||
        normalize(c.email).includes(q) ||
        normalize(c.phone).includes(q)
      );
    });

    rows = rows.sort((a, b) => {
      const an = `${a.lastName ?? ""} ${a.firstName ?? ""}`.trim().toLowerCase();
      const bn = `${b.lastName ?? ""} ${b.firstName ?? ""}`.trim().toLowerCase();
      if (sortBy === "name-asc") return an.localeCompare(bn);
      if (sortBy === "name-desc") return bn.localeCompare(an);
      if (sortBy === "specialty-asc") return (a.specialization || "").localeCompare(b.specialization || "");
      return 0;
    });

    return rows;
  }, [coaches, debouncedSearch, sortBy, specialtyFilter]);

  const totals = useMemo(() => ({
    all: coaches.length,
    filtered: filtered.length,
  }), [coaches.length, filtered.length]);

  const handleCoachAdded = () => fetchCoaches();

  const renderTable = () => (
    <div className="rounded-md border overflow-hidden hidden md:block">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("name", { defaultValue: "Nom" })}</TableHead>
            <TableHead>{t("specialty", { defaultValue: "Spécialité" })}</TableHead>
            <TableHead className="hidden lg:table-cell">{t("email", { defaultValue: "Email" })}</TableHead>
            <TableHead className="hidden lg:table-cell">{t("phone", { defaultValue: "Téléphone" })}</TableHead>
            <TableHead className="text-center">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 cursor-help">
                    <Bell className="h-4 w-4" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {t("deviceRegisteredForNotifications", { defaultValue: "Appareil mobile enregistré pour les notifications" })}
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead className="text-right">{t("common:actions", { defaultValue: "Actions" })}</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {isLoading && Array.from({ length: 6 }).map((_, i) => (
            <TableRow key={`sk-${i}`}>
              <TableCell colSpan={7} className="p-0">
                <div className="flex items-center gap-3 px-4 py-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <Skeleton className="h-4 w-[20%]" />
                  <Skeleton className="h-4 w-[15%]" />
                  <Skeleton className="h-4 w-[25%]" />
                </div>
              </TableCell>
            </TableRow>
          ))}

          {!isLoading && filtered.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-6 text-sm text-muted-foreground">
                {search || specialtyFilter !== "all"
                  ? t("noResultFor", { defaultValue: "Aucun coach pour cette recherche." })
                  : t("noCoaches", { defaultValue: "Aucun coach pour le moment." })}
              </TableCell>
            </TableRow>
          )}

          {!isLoading && filtered.map((coach) => (
            <TableRow key={coach.id} className="hover:bg-muted/50">
              <TableCell className="font-medium">
                <div className="flex items-center gap-3">
                  <Avatar className="h-9 w-9">
                    {/* `get_coaches` ne renvoie pas `profile_photo_url` : on ne
                        monte l'image que si le champ existe réellement, sinon
                        <AvatarImage src=""> reste en échec permanent. */}
                    {coach.profileImage && <AvatarImage src={coach.profileImage} />}
                    <AvatarFallback>
                      {(coach.firstName?.[0] || "").toUpperCase()}
                      {(coach.lastName?.[0] || "").toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div>{`${coach.firstName} ${coach.lastName}`}</div>
                    <div className="lg:hidden text-xs text-muted-foreground">{coach.email}</div>
                  </div>
                </div>
              </TableCell>

              <TableCell>
                <Badge variant="secondary" className="rounded-full">
                  {coach.specialization || "—"}
                </Badge>
              </TableCell>

              <TableCell className="hidden lg:table-cell">{coach.email}</TableCell>
              <TableCell className="hidden lg:table-cell">{coach.phone || "—"}</TableCell>

              <TableCell className="text-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    {deviceStatus?.[coach.user_id] ? (
                      <Bell className="h-4 w-4 text-green-500 mx-auto" />
                    ) : (
                      <BellOff className="h-4 w-4 text-destructive mx-auto" />
                    )}
                  </TooltipTrigger>
                  <TooltipContent>
                    {deviceStatus?.[coach.user_id]
                      ? t("deviceRegisteredCanReceive", { defaultValue: "Appareil enregistré - peut recevoir les notifications" })
                      : t("noDeviceCannotReceive", { defaultValue: "Aucun appareil - ne peut pas recevoir les notifications" })}
                  </TooltipContent>
                </Tooltip>
              </TableCell>

              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 px-2">
                      {t("common:actions", { defaultValue: "Actions" })} <ChevronDown className="ml-1 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuLabel>{t("quickActions", { defaultValue: "Actions rapides" })}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to={`/admin/coaches/${coach.id}`}>{t("common:view", { defaultValue: "Voir" })}</Link>
                    </DropdownMenuItem>
                    {/* ajoute d’autres items si nécessaire */}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const renderMobileCards = () => (
    <div className="grid grid-cols-1 gap-3 md:hidden">
      {isLoading && Array.from({ length: 6 }).map((_, i) => (
        <Card key={`m-sk-${i}`}>
          <CardContent className="p-4 flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </CardContent>
        </Card>
      ))}

      {!isLoading && filtered.length === 0 && (
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground">
            {search || specialtyFilter !== "all"
              ? t("noResultFor", { defaultValue: "Aucun coach pour cette recherche." })
              : t("noCoaches", { defaultValue: "Aucun coach pour le moment." })}
          </CardContent>
        </Card>
      )}

      {!isLoading && filtered.map((coach) => (
        <Card key={coach.id} className="hover:bg-muted/40 transition">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                {coach.profileImage && <AvatarImage src={coach.profileImage} />}
                <AvatarFallback>
                  {(coach.firstName?.[0] || "").toUpperCase()}
                  {(coach.lastName?.[0] || "").toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold truncate">
                    {coach.firstName} {coach.lastName}
                  </div>
                  <Badge variant="secondary" className="rounded-full shrink-0">{coach.specialization || "—"}</Badge>
                </div>

                <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1 truncate"><Mail className="h-3 w-3" /> {coach.email}</span>
                  {coach.phone && (
                    <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {coach.phone}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Dumbbell className="h-3 w-3" /> —</span>
                <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> —</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {deviceStatus?.[coach.user_id] ? (
                      <span className="inline-flex items-center gap-1 text-green-500">
                        <Bell className="h-3 w-3" />
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-destructive">
                        <BellOff className="h-3 w-3" />
                      </span>
                    )}
                  </TooltipTrigger>
                  <TooltipContent>
                    {deviceStatus?.[coach.user_id]
                      ? t("deviceRegistered", { defaultValue: "Appareil enregistré" })
                      : t("noDevice", { defaultValue: "Pas d'appareil" })}
                  </TooltipContent>
                </Tooltip>
              </div>
              <Button asChild variant="ghost" size="sm" className="h-8 px-2">
                <Link to={`/admin/coaches/${coach.id}`} className="inline-flex items-center">
                  {t("common:view", { defaultValue: "Voir" })} <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  return (
    <TooltipProvider>
    <div className="min-h-screen flex flex-col w-full">
      <main className="flex-1 p-4 md:p-6 max-w-7xl mx-auto w-full animate-fade-in">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t("title", { defaultValue: "Coaches" })}</h1>
            <p className="text-muted-foreground">
              {t("subtitle", { defaultValue: "Gérez vos coaches, spécialités et coordonnées." })}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" className="h-9" onClick={() => setIsAddCoachOpen(true)}>
              <PlusCircle className="mr-2 h-4 w-4" />
              {t("addCoach", { defaultValue: "Ajouter un coach" })}
            </Button>
          </div>
        </div>

        {/* Barre d’outils */}
        <Card className="animate-slide-in mb-4">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-3 md:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder={t("searchCoaches", { defaultValue: "Rechercher un coach..." })}
                  className="pl-8"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Tri */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="h-9 gap-2">
                    <ArrowUpDown className="h-4 w-4" />
                    {t("sort", { defaultValue: "Trier" })}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>{t("sortBy", { defaultValue: "Trier par" })}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setSortBy("name-asc")}>
                    {t("name", { defaultValue: "Nom" })} A → Z
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy("name-desc")}>
                    {t("name", { defaultValue: "Nom" })} Z → A
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy("specialty-asc")}>
                    {t("specialty", { defaultValue: "Spécialité" })} A → Z
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Le bouton n'avait aucune action (« Placeholder futur filtres ») :
                  il filtre maintenant sur la spécialité. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="default" className="h-9 gap-2">
                    <Filter className="h-4 w-4" />
                    <span>
                      {specialtyFilter === "all"
                        ? t("common:filter", { defaultValue: "Filtrer" })
                        : specialtyFilter}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 max-h-72 overflow-y-auto">
                  <DropdownMenuLabel>
                    {t("specialty", { defaultValue: "Spécialité" })}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setSpecialtyFilter("all")}>
                    {t("common:all", { defaultValue: "Toutes" })}
                  </DropdownMenuItem>
                  {specialties.map((s) => (
                    <DropdownMenuItem key={s} onClick={() => setSpecialtyFilter(s)}>
                      {s}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Compteurs */}
            <div className="mt-3 text-xs text-muted-foreground">
              {t("results", { defaultValue: "Résultats" })}: {totals.filtered} / {totals.all}
            </div>
          </CardContent>
        </Card>

        {/* Desktop table */}
        {renderTable()}

        {/* Mobile cards */}
        {renderMobileCards()}
      </main>

      <AddCoachDialog
        open={isAddCoachOpen}
        onOpenChange={setIsAddCoachOpen}
        onCoachAdded={handleCoachAdded}
      />
    </div>
    </TooltipProvider>
  );
};

export default Coaches;
